/**
 * Products Data - Dynamic Tebex Store Products
 *
 * Catalogue identity, commerce, and media come from Tebex via our backend.
 * Editorial copy is normalized from the versioned retroMeta payload by the
 * shared environment-neutral codec in retroMeta.js.
 */

import { formatPrice } from '@/utils/currency';
import {
  DEFAULT_BACKEND_URL,
  buildEditorialDefinition,
  extractImagesFromDescription,
  extractRetroMeta,
  extractYouTubeId,
  normalizeRetroMeta,
  optimizeTebexImageUrl,
  parseRetroMeta,
  slugifyProductName,
  stripHtml
} from './retroMeta';
import { isGemPackage } from './retroFootball';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || DEFAULT_BACKEND_URL).replace(/\/+$/, '');

const optimizeProductImageUrl = (url, width = 960) => (
  optimizeTebexImageUrl(url, width, BACKEND_URL)
);

const getTimestamp = (value) => {
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const buildShortDescription = (value) => {
  if (!value) return '';
  const trimmed = String(value).trim();
  return trimmed.length <= 150 ? trimmed : `${trimmed.substring(0, 150)}...`;
};

const sortProductsByCreatedAtDesc = (productList) => [...productList].sort((left, right) => {
  const topSellerDifference = Number(right.topSeller === true) - Number(left.topSeller === true);
  if (topSellerDifference !== 0) return topSellerDifference;

  const createdAtDifference = (right.createdAtTimestamp || 0) - (left.createdAtTimestamp || 0);
  if (createdAtDifference !== 0) return createdAtDifference;

  const orderDifference = (left.sortOrder || 0) - (right.sortOrder || 0);
  if (orderDifference !== 0) return orderDifference;

  return (left.title || '').localeCompare(right.title || '');
});

/**
 * Transform a native Tebex package into the long-standing storefront product
 * shape and attach its complete editorial definition. The optional arguments
 * are pure data so callers can provide local overflow media during migration.
 */
export const transformTebexPackage = (pkg, {
  catalogMeta = {},
  extraMedia = [],
  defaults = {}
} = {}) => {
  const description = pkg?.description || '';
  const structuredMeta = parseRetroMeta(description);
  const editorial = buildEditorialDefinition(pkg, {
    meta: structuredMeta,
    catalogMeta,
    extraMedia,
    defaults,
    backendUrl: BACKEND_URL
  });
  const commerce = editorial.commerce;
  const editorialImages = editorial.media.filter(item => item.type === 'image');
  const editorialVideo = editorial.media.find(item => item.type === 'video');
  const fullDescriptionText = editorial.overview.body
    || editorial.overview.intro
    || stripHtml(description);
  const shortDescriptionSource = editorial.overview.intro
    || editorial.overview.body
    || fullDescriptionText;
  const primaryImage = editorialImages[0]?.src || optimizeProductImageUrl(pkg.image, 960);
  const createdAt = pkg.created_at || null;
  const updatedAt = pkg.updated_at || null;

  return {
    id: pkg.id,
    slug: editorial.slug,
    aliases: editorial.aliases,
    tebexPackageId: pkg.id,
    title: editorial.title,
    description: buildShortDescription(shortDescriptionSource),
    fullDescription: fullDescriptionText,
    image: primaryImage,
    thumbnail: primaryImage,
    images: editorialImages.map(item => item.src),
    editorialMedia: editorial.media,
    youtubeId: editorialVideo?.youtubeId || editorialVideo?.url || null,
    video: null,
    category: editorial.category,
    tags: editorial.tags.length > 0 ? editorial.tags : [editorial.category],
    price: formatPrice(commerce.price.amount, commerce.price.currency),
    basePrice: commerce.price.amount,
    totalPrice: commerce.price.totalAmount,
    currency: commerce.price.currency,
    discount: commerce.price.discount,
    featured: true,
    topSeller: editorial.topSeller,
    isBundle: structuredMeta?.bundle?.isBundle === true || (structuredMeta?.bundle?.items?.length || 0) > 0,
    bundleKey: structuredMeta?.bundle?.key || null,
    bundleLabel: structuredMeta?.bundle?.label || null,
    bundleItemIds: structuredMeta?.bundle?.items || [],
    bundleItems: [],
    bundleMemberships: structuredMeta?.bundles || [],
    createdAt,
    updatedAt,
    createdAtTimestamp: getTimestamp(createdAt),
    updatedAtTimestamp: getTimestamp(updatedAt),
    sortOrder: Number.isFinite(pkg.order) ? pkg.order : 0,
    meta: structuredMeta,
    editorial,
    commerce,
    catalogFresh: commerce.fresh,
    catalogStale: commerce.stale,
    availableForPurchase: commerce.purchasable,
    disableGifting: commerce.disableGifting,
    disableQuantity: commerce.disableQuantity,
    features: editorial.features,
    requirements: editorial.requirements,
    faq: editorial.faq
  };
};

export const resolveBundleRelationships = (productList) => {
  const productsByPackageId = new Map();
  productList.forEach(product => {
    productsByPackageId.set(Number(product.tebexPackageId || product.id), product);
  });

  const bundles = productList.filter(product => product.isBundle);

  return productList.map(product => {
    const resolvedBundleItems = product.isBundle
      ? product.bundleItemIds
          .map(id => productsByPackageId.get(Number(id)))
          .filter(Boolean)
      : [];

    const inferredMemberships = bundles
      .filter(bundle => bundle.id !== product.id && bundle.bundleItemIds.includes(Number(product.tebexPackageId || product.id)))
      .map(bundle => ({
        id: bundle.tebexPackageId || bundle.id,
        key: bundle.bundleKey || null,
        label: bundle.bundleLabel || bundle.title,
        product: bundle
      }));

    const explicitMemberships = (product.bundleMemberships || []).map(membership => {
      const bundleProduct = membership.id ? productsByPackageId.get(Number(membership.id)) : null;
      return {
        ...membership,
        label: membership.label || bundleProduct?.bundleLabel || bundleProduct?.title || null,
        product: bundleProduct || null
      };
    });

    const membershipsByKey = new Map();
    [...explicitMemberships, ...inferredMemberships].forEach(membership => {
      const key = membership.id ? `id:${membership.id}` : `key:${membership.key || membership.label}`;
      if (!membershipsByKey.has(key)) membershipsByKey.set(key, membership);
    });

    return {
      ...product,
      bundleItems: resolvedBundleItems,
      bundleMemberships: Array.from(membershipsByKey.values())
    };
  });
};

const removeIapRelationships = (product) => ({
  ...product,
  bundleItemIds: (product.bundleItemIds || []).filter(itemId => !isGemPackage(itemId)),
  bundleItems: (product.bundleItems || []).filter(item => !isGemPackage(item)),
  bundleMemberships: (product.bundleMemberships || []).filter(membership => (
    !isGemPackage(membership?.id)
    && !isGemPackage(membership?.product)
  ))
});

/**
 * Keep Fantasy Gem SKUs available to the dedicated Football IAP surface while
 * excluding them from every generic storefront collection.
 */
export const partitionStorefrontProducts = (productList = []) => {
  const normalizedList = Array.isArray(productList) ? productList : [];
  return {
    catalogProducts: normalizedList
      .filter(product => !isGemPackage(product))
      .map(removeIapRelationships),
    iapProducts: normalizedList.filter(isGemPackage)
  };
};

let cachedProducts = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000;

/** Fetch and transform the current backend catalogue. */
export const fetchProducts = async () => {
  const now = Date.now();
  if (cachedProducts && (now - cacheTimestamp < CACHE_DURATION)) return cachedProducts;

  try {
    const { getPackages } = await import('@/services/TebexService');
    const tebexPackages = await getPackages();
    const catalogMeta = tebexPackages.catalogMeta || {};
    const transformedProducts = resolveBundleRelationships(sortProductsByCreatedAtDesc(
      tebexPackages.map(pkg => transformTebexPackage(pkg, { catalogMeta }))
    ));

    cachedProducts = transformedProducts;
    cacheTimestamp = now;
    return transformedProducts;
  } catch (error) {
    console.error('Failed to fetch products from Tebex:', error);
    if (!cachedProducts) throw error;

    // Never leak the previously fresh purchase state after a failed refresh.
    // Keep the editorial catalogue visible, but make every commerce action
    // explicitly stale/non-purchasable until a live backend response succeeds.
    const staleProducts = cachedProducts.map(product => {
      const commerce = {
        ...product.commerce,
        fresh: false,
        stale: true,
        purchasable: false
      };
      return {
        ...product,
        commerce,
        editorial: product.editorial ? { ...product.editorial, commerce } : product.editorial,
        catalogFresh: false,
        catalogStale: true,
        availableForPurchase: false,
        bundleItems: [],
        bundleMemberships: (product.bundleMemberships || []).map(membership => {
          const membershipWithoutProduct = { ...membership };
          delete membershipWithoutProduct.product;
          return membershipWithoutProduct;
        })
      };
    });
    cachedProducts = resolveBundleRelationships(staleProducts);
    return cachedProducts;
  }
};

export let carouselProducts = [];
export let allProducts = [];
export let products = [];
export let iapProducts = [];
export let filters = ['All Products'];

export const loadProducts = async () => {
  const loadedProducts = await fetchProducts();
  const partitionedProducts = partitionStorefrontProducts(loadedProducts);
  const storefrontProducts = partitionedProducts.catalogProducts;

  carouselProducts = storefrontProducts;
  allProducts = storefrontProducts;
  products = storefrontProducts;
  iapProducts = partitionedProducts.iapProducts;
  const bundleProducts = storefrontProducts.filter(product => product.isBundle);
  const categories = new Set();
  storefrontProducts.forEach(product => {
    if (product.category) categories.add(product.category);
  });
  filters = ['All Products', ...Array.from(categories).sort()];

  return {
    carouselProducts: storefrontProducts,
    allProducts: storefrontProducts,
    products: storefrontProducts,
    iapProducts,
    bundleProducts,
    filters
  };
};

export const filterProducts = (filter, productList = null) => {
  const list = productList || allProducts;
  if (filter === 'All Products') return list;
  return list.filter(product => product.category === filter);
};

// Compatibility aliases for consumers that predate the shared codec.
const extractImagesFromDesc = extractImagesFromDescription;
const extractStructuredMeta = extractRetroMeta;
const normalizeStructuredMeta = normalizeRetroMeta;

export const tebexHelpers = {
  extractYouTubeId,
  extractImagesFromDesc,
  stripHtml,
  optimizeProductImageUrl,
  slugifyName: slugifyProductName,
  transformTebexPackage,
  extractStructuredMeta,
  normalizeStructuredMeta,
  parseRetroMeta,
  buildEditorialDefinition
};
